import { apiGet, apiPost, apiPut, apiDelete } from '@lib/api/client';
import type { CurrentUser, Paginated } from '@appTypes/api';
import type { LoginResponse } from '@lib/auth/storage';

export interface LoginPayload {
  user_name: string;
  pass: string;
}

export interface LoginApiResponse extends LoginResponse {
  user?: CurrentUser;
}

export interface RegisterPayload {
  user_name: string;
  pass: string;
  display_name: string;
  email: string;
  code: string;
  phone?: string;
  timeZone?: string;
  agent_pin_code?: string;
  no_agent_support?: boolean;
  branches?: string[];
  departments?: string[];
}

export interface RegisterApiResponse {
  message?: string;
  token?: string;
  permissions?: string[];
  pendingApproval?: boolean;
}

// Shape returned by GET /api/users/search-agents — the backend selects
// `name pin_code phone email` (see controllers/userController.js → searchAgents),
// matching the legacy register flow. NOTE: these are agent-record fields
// (`name`/`pin_code`), NOT the user fields (`display_name`/`agent_pin_code`).
export interface Agent {
  _id: string;
  name?: string;
  pin_code?: string;
  phone?: string;
  email?: string;
}

export interface SessionInfo {
  _id: string;
  user?: string;
  deviceInfo?: {
    userAgent?: string;
    ip?: string;
    browser?: string;
    os?: string;
    device?: string;
  };
  isActive?: boolean;
  lastActivity?: string;
  createdAt?: string;
  expiresAt?: string;
}

export interface LoginHistoryEntry {
  _id: string;
  user?: string | { _id: string; display_name?: string };
  action: 'login' | 'logout' | 'token_refresh' | 'login_failed' | 'force_logout' | string;
  deviceInfo?: SessionInfo['deviceInfo'];
  sessionId?: string;
  success?: boolean;
  failReason?: string;
  performedBy?: string;
  createdAt: string;
}

export interface LoginHistoryResponse {
  history: LoginHistoryEntry[];
  total: number;
  page: number;
  totalPages: number;
}

export interface MyLicense {
  sub?: string;
  planName?: string;
  maxEmployees?: number;
  maxDevices?: number;
  issuedAt?: string;
  expiresAt?: string;
  licenseId?: string;
}

export const authApi = {
  login: (payload: LoginPayload) => apiPost<LoginApiResponse, LoginPayload>('/api/users/login', payload),
  logout: (refreshToken: string) =>
    apiPost<{ success?: boolean; message?: string }, { refreshToken: string }>(
      '/api/users/logout',
      { refreshToken },
    ),
  refreshToken: (refreshToken: string) =>
    apiPost<{ accessToken: string; expiresIn: number }, { refreshToken: string }>(
      '/api/users/refresh-token',
      { refreshToken },
    ),

  profile: () => apiGet<CurrentUser>('/api/users/profile'),
  updateProfile: (body: Partial<CurrentUser> & Record<string, unknown>) =>
    apiPut<CurrentUser, typeof body>('/api/users/profile', body),
  changePassword: (newPassword: string) =>
    apiPut<CurrentUser, { pass: string }>('/api/users/profile', { pass: newPassword }),
  myLicense: () =>
    apiGet<{ ok?: boolean; license?: MyLicense } | MyLicense>('/api/users/my-license'),

  register: (payload: RegisterPayload) =>
    apiPost<RegisterApiResponse, RegisterPayload>('/api/users/register', payload),
  verifyEmail: (email: string) =>
    apiPost<{ success: boolean; message?: string }, { email: string }>(
      '/api/users/verify-email',
      { email },
    ),
  searchAgents: (q: string) =>
    apiGet<Agent[] | { agents: Agent[] }>(`/api/users/search-agents?q=${encodeURIComponent(q)}`),

  forgotSendOtp: (email: string) =>
    apiPost<{ success: boolean; message?: string }, { email: string }>(
      '/api/users/forgot-password/send-otp',
      { email },
    ),
  forgotReset: (payload: { email: string; otp: string; newPassword: string }) =>
    apiPost<{ success: boolean; message?: string }, typeof payload>(
      '/api/users/forgot-password/reset',
      payload,
    ),

  sessions: () => apiGet<{ sessions: SessionInfo[] } | SessionInfo[]>('/api/users/sessions'),
  terminateSession: (id: string) =>
    apiDelete<{ success: boolean; message?: string }>(`/api/users/sessions/${id}`),
  terminateAllOtherSessions: (userId: string) =>
    apiDelete<{ success: boolean; message?: string }>(`/api/users/${userId}/sessions/all`),

  loginHistory: (params: { page?: number; limit?: number } = {}) => {
    const { page = 1, limit = 20 } = params;
    return apiGet<LoginHistoryResponse>(
      `/api/users/login-history?limit=${limit}&page=${page}`,
    );
  },
};

export type PaginatedHistory = Paginated<LoginHistoryEntry>;
